import { useEffect, useRef } from "react";
import axios, { AxiosError, AxiosResponse } from "axios";
import { Arguments, MutatorCallback, MutatorOptions } from "swr";
import useSWRInfinite from "swr/infinite";

import { CarriersListResponse } from "models/dto/CarriersListResponse";
import { QueryGridResponse } from "models/QueryGridResponse";
import { CarriersFilter } from "./useCarriersFilter";
import { appendCarrierFilterParams } from "./carrierFilterParams";

const pageSize = 25;

// SWR does not export these which is making it a PITA to type
type SWRInfiniteRevalidateFn<Data> = (data: Data, key: Arguments) => boolean;

interface SWRInfiniteMutatorOptions<Data, MutationData = Data>
  extends Omit<MutatorOptions<Data, MutationData>, "revalidate"> {
  revalidate?:
    | boolean
    | SWRInfiniteRevalidateFn<Data extends unknown[] ? Data[number] : never>;
}

export type InfiniteKeyedMutator<Data> = <MutationData = Data>(
  data?: Data | Promise<Data | undefined> | MutatorCallback<Data>,
  opts?: boolean | SWRInfiniteMutatorOptions<Data, MutationData>
) => Promise<Data | MutationData | undefined>;

const useGetCarriersInfinite = ({
  cancelRequest,
  carriersFilter,
}: {
  cancelRequest?: boolean;
  carriersFilter: CarriersFilter;
}): {
  carriers: CarriersListResponse[];
  totalCarriers: number;
  isLoading: boolean;
  getNextPage: () => void;
  reloadCarriers: InfiniteKeyedMutator<
    QueryGridResponse<CarriersListResponse>[]
  >;
} => {
  const abortController = useRef<AbortController>();
  const baseUrl = "/api/carriers/list";

  useEffect(() => {
    if (cancelRequest && abortController.current) {
      abortController.current.abort();
    }
  }, [cancelRequest]);

  const getKey = (
    page: number,
    previousPageData: QueryGridResponse<CarriersListResponse>
  ) => {
    if (previousPageData && !previousPageData.entities.length) {
      return null;
    }

    const params = new URLSearchParams({
      page: (page + 1).toString(),
      pageSize: pageSize.toString(),
      sortField: carriersFilter.sortField,
      dir: carriersFilter.dir,
    });

    appendCarrierFilterParams(params, carriersFilter);

    return `${baseUrl}?${params.toString()}`;
  };

  const swr = useSWRInfinite<
    QueryGridResponse<CarriersListResponse>,
    AxiosError
  >(
    getKey,
    (url: string) => {
      abortController.current = new AbortController();

      return axios
        .get<
          QueryGridResponse<CarriersListResponse>,
          AxiosResponse<QueryGridResponse<CarriersListResponse>>
        >(url, { signal: abortController.current.signal })
        .then((response) => response.data);
    },
    {
      keepPreviousData: false,
      revalidateOnFocus: true,
      shouldRetryOnError: false,
    }
  );

  const carriers = swr.data?.flatMap((data) => data.entities) ?? [];

  return {
    carriers,
    totalCarriers: swr.data?.[0]?.filteredCount ?? 0,
    isLoading:
      swr.isLoading ||
      (swr.isValidating && carriers.length === 0) ||
      (!swr.error && swr.size > 0 && !swr.data?.[swr.size - 1]),
    getNextPage: () => {
      swr.setSize(swr.size + 1);
    },
    reloadCarriers: swr.mutate,
  };
};

export default useGetCarriersInfinite;
